import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { StateService } from '@uirouter/core';

import { CimaCore, LocalStorage, Util, XmStore, XmStoreUtil } from 'core/services';
import { SessionStorage } from 'core/services/storage/session';
import { Account, StreetAddress, User } from 'store/user/models';
import { CmsCore, CmsMyAccount, CmsMyAccountModals } from 'store/cms/models';
import { MyAccountModal } from 'my-account/services/modal';
import { CbmPaymentContentUtil } from 'my-account/services/cbm-payment-content';
import { XmMyAccountAnnouncements } from 'my-account/shared/modals/announcement/announcement';
import { CONFIG_TOKEN, StorageToken } from 'core/constants';
import { IXMOptions } from 'core/interfaces';

@Component({
    selector: 'xm-my-account',
    styleUrls: ['./base.scss'],
    templateUrl: './base.html',
    standalone: false
})
export class MyAccountBaseComponent implements OnInit, OnDestroy {
    public currentBanner: CmsNotificationBanner;
    public expanded: boolean;
    public activeTabName: string;
    public isBusinessUser: boolean;
    public serviceAddress: StreetAddress;
    public account: Account;
    public enableNotificationBanner: boolean = false;
    public pageData: CmsNavMenuItems;
    public enableProdOutageBanner: boolean;
    public isOrionTenant: boolean = false;
    // TODO: Remove code for hiding 'Xfinity Mobile app is going away soon' banner
    // once the app goes away. See MCXW-9907.
    private sessionStorage: SessionStorage;

    private cimaCore: CimaCore;
    private xmStore: XmStore;
    private bannerTimeout: NodeJS.Timer;
    private subscriptions: Subscription[] = [];
    private notificationBanners: CmsNotificationBanner[];
    private prodOutageBanner: CmsNotificationBanner;
    private accountModal: MyAccountModal;
    private localStorage: LocalStorage;
    private customerId: string;
    private config: IXMOptions;
    private state: StateService;

    constructor(cimaCore: CimaCore, xmStore: XmStore, accountModal: MyAccountModal, sessionStorage: SessionStorage, localStorage: LocalStorage, @Inject(CONFIG_TOKEN) config: IXMOptions, state: StateService) {
        Object.assign(this, { cimaCore, xmStore, accountModal, sessionStorage, localStorage, config, state });
    }

    public ngOnInit(): void {
        this.isBusinessUser = this.cimaCore.isBusinessUser;
        this.enableProdOutageBanner = this.config.ENABLE_PROD_OUTAGE_BANNER;
        this.enableNotificationBanner = this.config.ENABLE_NOTIFICATION_BANNER;
        this.isOrionTenant = this.sessionStorage.get(StorageToken.X_TENANT_ID)=== 'ORION';
        CbmPaymentContentUtil.setOrionTenant(this.isOrionTenant);
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsCore>(CmsCore), (result: CmsCore) => {
            this.pageData = { ...result.nav.myAccount };
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peekChild<Account, User>('account', Account, User), (account: Account) => {
            this.account = account;
            this.serviceAddress = account.serviceAddress;
            this.customerId = account.customerId;
            CbmPaymentContentUtil.setAccountAuthGuid(this.account.authGuid);
            this.getContent();
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsMyAccountModals>(CmsMyAccountModals), (result: CmsMyAccountModals) => {
            const announcementArray: Announcement[] = result.modals.announcement.announcementsArray.filter((announcement: Announcement) => announcement.alwaysShow || (this.isWithinTimeRange(announcement) && this.notSeen(announcement)));

            if (announcementArray.length) {
                this.accountModal.open(XmMyAccountAnnouncements, announcementArray);
            }
        }));
    }

    public closeBanner(): void {
        this.expanded = false;
    }

    public getActiveTabName(tabName: string): void {
        this.activeTabName = tabName;
    }

    public ngOnDestroy(): void {
        clearTimeout(this.bannerTimeout);
        Util.unsubscribeAll(this.subscriptions);
    }

    public get displayNotificationBanner(): boolean {
        if (this.enableProdOutageBanner) {
            this.currentBanner = this.prodOutageBanner;

            return true;
        }

        // The level property should match the exact string coming from the API
        this.currentBanner = this.account && this.notificationBanners && this.notificationBanners.find((banner: CmsNotificationBanner) => banner.level === this.account.level);

        if (!this.currentBanner) {
            return false;
        }

        const stateConditionForCBM: boolean = this.isBusinessUser ? this.currentBanner?.stateCondition?.includes(this.serviceAddress?.state) : true;
        const currentTime: number = Date.now();
        const startTime: number = new Date(this.currentBanner.startTime).getTime();
        const endTime: number = new Date(this.currentBanner.endTime).getTime();

        return this.currentBanner && currentTime >= startTime && currentTime <= endTime && stateConditionForCBM && this.enableNotificationBanner;
    }

    public get hideBreadcrumbs(): boolean {       
        const hideCompList: string[] = ['account.billing.statement-list.brite-bill', 'account.billing.bill-list.brite-bill'];

        if (this.cimaCore.cbmMandateSelectionMode && this.state?.current?.name === 'account.activity.switch-locations') {
            return true;
        }

        return hideCompList?.includes(this.state?.current?.name);
    }

    public get showMyAccountMenu(): boolean {
        return !this.cimaCore.cbmMandateSelectionMode;
    }

    private getContent(): void {
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsMyAccount>(CmsMyAccount), (cmsMyAccount: CmsMyAccount) => {
            this.notificationBanners = this.isBusinessUser ? cmsMyAccount.notificationBannerCBM : cmsMyAccount.notificationBannerResi;
            this.prodOutageBanner = cmsMyAccount.prodOutageNotificationBanner;
            this.bannerTimeout = setTimeout(() => {
                this.expanded = true;
            }, 3000);
        }));
    }

    private isWithinTimeRange(announcement: Announcement): boolean {
        if (announcement.startTime && announcement.endTime) {
            return new Date().getTime() >= new Date(announcement.startTime).getTime() && new Date().getTime() <= new Date(announcement.endTime).getTime();
        } else {
            return true;
        }
    }

    private notSeen(announcement: Announcement): boolean {
        if (this.localStorage.hasKey('seenAnnouncement')) {
            const seenAnnouncementObject: object = JSON.parse(atob(this.localStorage.get('seenAnnouncement')));
            const customerAnnouncementKey: string = this.customerId + announcement.announcmentId;

            /* Arjun please check this, working? */
            return !Object.prototype.hasOwnProperty.call(seenAnnouncementObject, customerAnnouncementKey) || seenAnnouncementObject[customerAnnouncementKey] > 0;
        } else {
            return true;
        }
    }
}
